import React, { useEffect, useMemo, useState } from "react";
import { useChatFeedbackLock } from './FarChat/ChatFeedbackOverlays';
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Badge,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import RaTrainingTile from './RaTrainingTile';
import { MOBILE_DENSITY, phoneMedia } from '../theme/mobileDensity';

import { listAccessibleFlows } from "../api/flowFunctions";
import { fetchWorkflowNotificationCounts } from '../api/workflowNotifications';
import {
  getCachedExtraction,
  getCachedSearchResults,
} from "../api/wageDetFunctions";
import type { FlowMetadata } from "../types/flow";
import {
  deriveFlowStatus,
  deriveLastActiveStageText,
  getFlowTypeChip,
  sortFlows,
} from "./FlowTable";

interface RecentWorkflowsProps {
  canAccessRaTraining?: boolean;
  onOpenRaTraining?: () => void;
  canAccessTraining?: boolean;
  onOpenTraining?: () => void;
  allowedWorkflowTypes?: string[] | null;
  canUseDcmaPbp?: boolean;
  canUseDcmaCrr?: boolean;
  onOpen: (
    flowId: string,
    flowName: string,
    workflowType?: FlowMetadata["workflow_type"],
  ) => void;
  onViewAll: () => void;
  onOpenNotifications?: (flowId: string, flowName: string) => void;
}

function relativeUpdatedAt(value?: string): string {
  if (!value) return "Recently updated";
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return "Recently updated";
  const elapsedMinutes = Math.max(
    0,
    Math.floor((Date.now() - timestamp) / 60000),
  );
  if (elapsedMinutes < 1) return "Updated just now";
  if (elapsedMinutes < 60) return `Updated ${elapsedMinutes}m ago`;
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) return `Updated ${elapsedHours}h ago`;
  const elapsedDays = Math.floor(elapsedHours / 24);
  if (elapsedDays < 7) return `Updated ${elapsedDays}d ago`;
  return `Updated ${new Date(timestamp).toLocaleDateString()}`;
}

function effectiveWorkflowType(
  flow: FlowMetadata,
): NonNullable<FlowMetadata["workflow_type"]> {
  return flow.workflow_type || "market_research";
}

function hasMarketResearchEditAwareness(flow: FlowMetadata): boolean {
  return effectiveWorkflowType(flow) === 'market_research'
    && flow.market_research_has_unreviewed_other_edit === true;
}

function completedStageCountText(flow: FlowMetadata, fallback: string): string {
  const progress = flow.workflow_progress;
  if (
    !progress ||
    !Number.isInteger(progress.completed_count) ||
    !Number.isInteger(progress.total_count) ||
    progress.completed_count < 0 ||
    progress.total_count <= 0 ||
    progress.completed_count > progress.total_count
  ) {
    return fallback;
  }
  return `${progress.completed_count}/${progress.total_count}`;
}

const RecentWorkflows: React.FC<RecentWorkflowsProps> = ({
  canAccessRaTraining = false,
  onOpenRaTraining,
  canAccessTraining = false,
  onOpenTraining,
  allowedWorkflowTypes,
  canUseDcmaPbp = false,
  canUseDcmaCrr = false,
  onOpen,
  onViewAll,
  onOpenNotifications,
}) => {
  const feedbackLocked = useChatFeedbackLock();
  const feedbackLockRef = React.useRef(feedbackLocked); feedbackLockRef.current = feedbackLocked;
  const [flows, setFlows] = useState<FlowMetadata[]>([]);
  const [cacheEvidence, setCacheEvidence] = useState<Record<string, {
    hasSearchResults?: boolean;
    hasExtraction?: boolean;
  }>>({});
  const [unreadNotificationCounts, setUnreadNotificationCounts] = useState<Record<string, number>>({});
  const [notificationCountsUnavailable, setNotificationCountsUnavailable] = useState(false);
  const [flowDiscoveryUnavailable, setFlowDiscoveryUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    let requestVersion = 0;
    let activeListController: AbortController | undefined;
    let activeListDeadline: number | undefined;
    let countVersion = 0;
    let pendingCountKey: string | null = null;
    let activeCountController: AbortController | undefined;
    let latestCountIds: string[] = [];
    const isCurrent = (version: number) => (
      !controller.signal.aborted && version === requestVersion
    );
    const refreshCounts = async (ids: string[], replacePending = false) => {
      latestCountIds = ids;
      if (feedbackLockRef.current) return;
      const key = JSON.stringify([...ids].sort());
      if (key === pendingCountKey && !replacePending) return;
      const version = ++countVersion;
      activeCountController?.abort();
      const countController = new AbortController();
      activeCountController = countController;
      pendingCountKey = key;
      const isCurrentCount = () => (
        !controller.signal.aborted && version === countVersion
      );
      try {
        const counts = ids.length > 0
          ? await fetchWorkflowNotificationCounts(ids, countController.signal) : {};
        if (isCurrentCount()) {
          setUnreadNotificationCounts(counts);
          setNotificationCountsUnavailable(false);
        }
      } catch {
        if (isCurrentCount()) {
          setNotificationCountsUnavailable(true);
        }
      } finally {
        if (isCurrentCount()) {
          pendingCountKey = null;
          activeCountController = undefined;
        }
      }
    };
    const refresh = async (refreshExistingCounts = true) => {
      const version = ++requestVersion;
      activeListController?.abort();
      window.clearTimeout(activeListDeadline);
      const listController = new AbortController();
      activeListController = listController;
      const deadline = window.setTimeout(() => {
        if (!isCurrent(version)) return;
        listController.abort();
        setFlowDiscoveryUnavailable(true);
        setLoading(false);
        void refresh();
      }, 120_000);
      activeListDeadline = deadline;
      try {
        const loadedFlows = await listAccessibleFlows(listController.signal);
        if (!isCurrent(version)) return;
        setFlows(loadedFlows);
        setLoading(false);
        setFlowDiscoveryUnavailable(false);
        const ids = loadedFlows.map((flow) => flow.id);
        setCacheEvidence((current) => Object.fromEntries(
          ids.filter((id) => current[id]).map((id) => [id, current[id]]),
        ));
        // Count latency must not prevent discovering newly shared workflows.
        // An unchanged ID set keeps its existing count request in flight.
        const idsChanged = JSON.stringify([...ids].sort()) !==
          JSON.stringify([...latestCountIds].sort());
        if (refreshExistingCounts || idsChanged) void refreshCounts(ids);
      } catch (error) {
        if (isCurrent(version)) {
          setFlowDiscoveryUnavailable((error as { status?: number })?.status !== 423);
        }
      } finally {
        window.clearTimeout(deadline);
        if (isCurrent(version)) {
          activeListController = undefined;
          setLoading(false);
        }
      }
    };
    const intervalId = window.setInterval(() => {
      if (!activeListController) void refresh();
    }, 60_000);
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const refreshAfterInboxChange = () => {
      void refreshCounts(latestCountIds, true);
      void refresh(false);
    };
    window.addEventListener('workflow-notifications-changed', refreshAfterInboxChange);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    void refresh();
    return () => {
      controller.abort();
      activeListController?.abort();
      activeCountController?.abort();
      window.clearTimeout(activeListDeadline);
      window.clearInterval(intervalId);
      window.removeEventListener('workflow-notifications-changed', refreshAfterInboxChange);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [allowedWorkflowTypes, canUseDcmaPbp, canUseDcmaCrr, feedbackLocked]);

  const recentFlows = useMemo(() => {
    const visible = flows.filter(
      (flow) => {
        const workflowType = effectiveWorkflowType(flow);
        return !flow.archived &&
        (workflowType === "dcma_crr" ? canUseDcmaCrr : workflowType === "dcma_pbp"
          ? canUseDcmaPbp
          : !allowedWorkflowTypes ||
            allowedWorkflowTypes.includes(workflowType));
      },
    );
    // An action request is more important than the ordinary updated-at sort.
    // Notification counts arrive through one access-checked batch request for
    // flows that this user can already see; do not infer a flow from the
    // private notification inbox or make one request per card. This lets an
    // older shared workflow still surface on Home when it needs attention.
    return sortFlows(visible.map((flow) => ({
      ...flow, ...cacheEvidence[flow.id],
    })), "updated", "desc")
      .sort((left, right) => {
        const leftUnread = unreadNotificationCounts[left.id] ?? 0;
        const rightUnread = unreadNotificationCounts[right.id] ?? 0;
        if (leftUnread === rightUnread) return 0;
        return rightUnread - leftUnread;
      })
      .slice(0, 4);
  }, [allowedWorkflowTypes, canUseDcmaPbp, canUseDcmaCrr, flows, cacheEvidence, unreadNotificationCounts]);

  // Keep the dependency independent of decoration, avoiding a fetch/render loop.
  const visibleWageIds = JSON.stringify(recentFlows
    .filter((flow) => flow.workflow_type === 'wage_determination')
    .map((flow) => flow.id));
  useEffect(() => {
    if (feedbackLocked) return;
    let cancelled = false;
    const ids: string[] = JSON.parse(visibleWageIds);
    for (const id of ids) {
      const reads = [
        ['hasSearchResults', getCachedSearchResults(id)],
        ['hasExtraction', getCachedExtraction(id)],
      ] as const;
      for (const [key, read] of reads) {
        void read.then((value) => {
          if (cancelled) return;
          setCacheEvidence((current) => ({
            ...current,
            [id]: { ...current[id], [key]: value !== null },
          }));
        }).catch(() => {
          // A failed read cannot invalidate previously known cache evidence.
        });
      }
    }
    return () => { cancelled = true; };
  }, [visibleWageIds, flows, feedbackLocked]);

  const showRaTraining = canAccessRaTraining && !!onOpenRaTraining;
  const trainingTileCount = Number(canAccessTraining) + Number(showRaTraining);

  if (!loading && recentFlows.length === 0 && !canAccessTraining && !showRaTraining &&
      !flowDiscoveryUnavailable && !notificationCountsUnavailable) return null;

  return (
    <Box component="section" aria-labelledby="recent-workflows-heading">
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 0.25,
          [phoneMedia(theme)]: {
            gap: 0.5,
            flexWrap: 'nowrap',
            '& > .MuiTypography-root': { minWidth: 0, flex: '1 1 0' },
          },
        })}
      >
        <Typography
          id="recent-workflows-heading"
          component="h2"
          variant="h3"
        >
          Pick up where you left off
        </Typography>
        <Button
          endIcon={<ArrowForwardIcon />}
          onClick={onViewAll}
          disabled={feedbackLocked}
          aria-disabled={feedbackLocked || undefined}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          View all
        </Button>
      </Box>

      {flowDiscoveryUnavailable && (
        <Typography role="status" variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Workflow list unavailable. Showing last known workflows, if any.
        </Typography>
      )}
      {notificationCountsUnavailable && (
        <Typography role="status" variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Notification counts unavailable. Showing last known activity.
        </Typography>
      )}

      <Box
        data-testid="recent-workflows-grid"
        sx={{
          display: "grid",
          gridAutoFlow: { xs: "column", md: "row" },
          gridAutoColumns: { xs: "82%", sm: "46%" },
          gridTemplateColumns: {
            md: "repeat(4, minmax(0, 1fr))",
            lg: `repeat(${4 + trainingTileCount}, minmax(0, 1fr))`,
          },
          gap: 1.5,
          overflowX: { xs: "auto", md: "visible" },
          pb: { xs: 1, md: 0 },
          scrollSnapType: { xs: "x mandatory", md: "none" },
        }}
      >
        {showRaTraining && <RaTrainingTile onOpen={onOpenRaTraining!} compact />}
        {canAccessTraining && (
          <Card variant="outlined" sx={{ minWidth: 0, borderRadius: 1.5, scrollSnapAlign: 'start', borderColor: 'primary.main', bgcolor: 'action.hover' }}>
            <CardActionArea aria-label="Open Training" disabled={feedbackLocked} aria-disabled={feedbackLocked || undefined} onClick={() => { if (!feedbackLockRef.current) onOpenTraining?.(); }} sx={{ height: '100%', minHeight: { xs: 128, sm: 148 }, p: { xs: MOBILE_DENSITY.cardInset, sm: 1.5 }, '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: -3 } }}>
              <Chip label="Warrant Training" size="small" color="primary" icon={<SchoolOutlinedIcon />} />
              <Typography variant="h5" component="h3" sx={{ mt: { xs: 1, sm: 1.5 } }}>Prepare for your warrant</Typography>
            </CardActionArea>
          </Card>
        )}
        {loading
          ? Array.from({ length: 4 }, (_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                height={156}
                sx={{ borderRadius: 1.5 }}
              />
            ))
          : recentFlows.map((flow) => {
              const typeChip = getFlowTypeChip(flow);
              const stageText = deriveLastActiveStageText(flow);
              const status = deriveFlowStatus(flow);
              const completedStageText = completedStageCountText(flow, stageText);
              const statusLabel = status === "in progress"
                ? `In progress${completedStageText && completedStageText !== "—" ? ` (${completedStageText})` : ""}`
                : status;
              const hasEditAwareness = hasMarketResearchEditAwareness(flow);
              return (
                <Card
                  key={flow.id}
                  variant="outlined"
                  sx={{
                    // Anchor the unread-notification control to this card.
                    // Without a positioned ancestor, its absolute placement is
                    // relative to the page and can sit under the app navbar.
                    position: "relative",
                    minWidth: 0,
                    borderRadius: 1.5,
                    scrollSnapAlign: "start",
                    transition:
                      "border-color 160ms ease, box-shadow 160ms ease",
                    "&:hover": { borderColor: "primary.main", boxShadow: 2 },
                  }}
                >
                  {(unreadNotificationCounts[flow.id] ?? 0) > 0 && onOpenNotifications && (
                    <IconButton
                      disabled={feedbackLocked}
                      size="small"
                      color="error"
                      aria-label={`${unreadNotificationCounts[flow.id]} unread workflow notification${unreadNotificationCounts[flow.id] === 1 ? '' : 's'} for ${flow.name || 'workflow'}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!feedbackLockRef.current) onOpenNotifications(flow.id, flow.name || 'Market Research workflow');
                      }}
                      sx={{ position: 'absolute', zIndex: 1, top: 8, right: 8 }}
                    >
                      <Badge color="error" variant="dot"><NotificationsNoneIcon fontSize="small" /></Badge>
                    </IconButton>
                  )}
                  <CardActionArea
                    disabled={feedbackLocked}
                    aria-disabled={feedbackLocked || undefined}
                    onClick={() => { if (!feedbackLockRef.current) onOpen(
                      flow.id,
                      flow.name || flow.id,
                      effectiveWorkflowType(flow),
                    ); }}
                    sx={{ height: "100%", p: { xs: MOBILE_DENSITY.cardInset, sm: 1.5 } }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <Chip
                        label={typeChip.label}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, ...(typeChip.sx as object) }}
                      />
                    </Box>
                    <Typography
                      variant="h5"
                      component="h3"
                      sx={{ mt: { xs: 1, sm: 1.5 }, overflowWrap: "anywhere", display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                      {flow.name || flow.id}
                      {hasEditAwareness && (
                        <Box
                          component="span"
                          role="img"
                          aria-label={`${flow.name || 'Workflow'} has updates from another collaborator`}
                          data-testid={`recent-workflow-edit-awareness-dot-${flow.id}`}
                          data-edit-awareness-tone="red"
                          title="Updated by another collaborator"
                          sx={{
                            backgroundColor: (theme) => theme.palette.marketResearch.editAwareness,
                            border: 0,
                            borderRadius: '50%',
                            display: 'inline-block',
                            flexShrink: 0,
                            height: 10,
                            outline: 0,
                            width: 10,
                          }}
                        />
                      )}
                    </Typography>
                    <Box
                      data-testid={`recent-workflow-progress-${flow.id}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      <Typography
                        data-testid={`recent-workflow-status-${flow.id}`}
                        variant="caption"
                        color={
                          status === "completed"
                            ? "success.main"
                            : "warning.main"
                        }
                        sx={{ fontWeight: 600, textTransform: "capitalize" }}
                      >
                        {statusLabel}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 1,
                      }}
                    >
                      <AccessTimeOutlinedIcon
                        sx={{ fontSize: 15, color: "text.disabled" }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {relativeUpdatedAt(flow.updatedAt)}
                      </Typography>
                    </Box>
                  </CardActionArea>
                </Card>
              );
            })}
      </Box>
    </Box>
  );
};

export default RecentWorkflows;
